import React, { useState, useEffect } from "react";
import { apiFetch } from "../../utils/apiFetch";
import {
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
} from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../../components/common/Button";

import calendar from "../../assets/draft/calendar.svg";
import list from "../../assets/draft/list.svg";
import arrowRight from "../../assets/scoreBoard/right-arrow.svg";
import arrowLeft from "../../assets/scoreBoard/left-arrow.svg";
import close from "../../assets/draft/close.svg";
import eye from "../../assets/posts/eye.svg";
import { useTranslation } from "react-i18next";
import VideoThumb from "../../video/VideoThumb";

const MONTH_KEYS = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december"
];
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const isMobileDevice = () => window.innerWidth < 1024;

const Scheduled = ({ email = "", accounts = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getMonthName = (monthIndex) => t(`calendar.months.${MONTH_KEYS[monthIndex]}`);
  const getShortMonthName = (monthIndex) => (t(`calendar.months.${MONTH_KEYS[monthIndex]}`) || "").slice(0, 3);
  const getDayName = (date) => {
    const dayIndex = (date.getDay() + 6) % 7; // convert Sunday=0 to Monday=0
    return t(`calendar.weekdays.${WEEKDAY_KEYS[dayIndex]}`);
  };

  const [showCalendar, setShowCalendar] = useState(location.state?.view === "calendar");
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null); // ← mobile modal
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date()); // ← mobile week
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [currentListMonth, setCurrentListMonth] = useState(new Date());
  const [drafts, setDrafts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [listFilter, setListFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const showProfileFilter = accounts.length > 1;
  const matchesProfile = (item) =>
    profileFilter === "all" || (item.target || "personal") === profileFilter;
  const visibleScheduled = scheduledPosts.filter(matchesProfile);
  const visiblePublished = publishedPosts.filter(matchesProfile);

  // Track screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scheduledMap = [...visibleScheduled, ...visiblePublished].reduce((acc, item) => {
    if (item.publication_date_time) {
      try {
        const dateKey = format(new Date(item.publication_date_time), "MMM dd");
        acc[dateKey] = acc[dateKey] ? [...acc[dateKey], item] : [item];
      } catch {}
    }
    return acc;
  }, {});

  const fetchCalendarPosts = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      const month = format(currentDate, "yyyy-MM");
      const response = await apiFetch(`${process.env.REACT_APP_GET_POSTS_URL}?month=${month}&limit=100`);
      if (!response.ok) throw new Error("Failed to fetch posts");
      const data = await response.json();
      setScheduledPosts(data.scheduled || []);
      setPublishedPosts(data.published || []);
      setDrafts(data.drafts || []);
    } catch (err) {
      console.error("❌ Fetch calendar posts failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchListPosts = async (showLoader = true) => {
    if (!email) return;
    if (showLoader) setIsLoading(true);
    try {
      const month = format(currentListMonth, "yyyy-MM");
      const response = await apiFetch(`${process.env.REACT_APP_GET_POSTS_URL}?month=${month}&limit=100`);
      if (!response.ok) throw new Error("Failed to fetch posts");
      const data = await response.json();
      setDrafts(data.drafts || []);
      setScheduledPosts(data.scheduled || []);
      setPublishedPosts(data.published || []);
    } catch (err) {
      console.error("❌ Fetch list posts failed:", err);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showCalendar && email) fetchCalendarPosts();
  }, [currentDate, email, showCalendar]);

  useEffect(() => {
    if (!showCalendar && email) fetchListPosts();
  }, [currentListMonth, email, showCalendar]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchListPosts();
    }
  }, [location.state]);

  useEffect(() => {
    if (!email) return;
    const interval = setInterval(() => {
      showCalendar ? fetchCalendarPosts(false) : fetchListPosts(false);
    }, 60000);
    return () => clearInterval(interval);
  }, [email, showCalendar]);

  const handleOpenPost = (post) => {
    navigate("/viewing", {
      state: {
        post: {
          id: post.id,
          status: post.status,
          target: post.target,
          text: post.post || [post.hook, post.body, post.cta].filter(Boolean).join("\n"),
          imageUrl: post.image_url,
          documentUrl: post.document_url,
          documentFilename: post.document_filename,
          videoId: post.video_id,
          videoUrl: post.video_url,
          videoPosterUrl: post.video_poster_url,
          hook: post.hook,
          body: post.body,
          cta: post.cta,
          hashtags: post.hashtags,
          publicationDateTime: post.publication_date_time,
        }
      }
    });
  };

  const getTargetLabel = (target) => {
    if (!target || target === "personal") return "Personal";
    const account = accounts.find(a => a.target === target);
    const label = account?.label || target;
    return label.length > 40 ? `${label.slice(0, 40)}…` : label;
  };
  const renderProfileFilter = () => {
    if (!showProfileFilter) return null;
    return (
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setProfileFilter("all")}
          className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
            profileFilter === "all"
              ? "bg-black-800 text-white"
              : "bg-black-20 text-black-600 hover:bg-black-40"
          }`}
        >
          {t("filter.allProfiles")}
        </button>
        {accounts.map((a) => (
          <button
            key={a.target}
            onClick={() => setProfileFilter(a.target)}
            className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
              profileFilter === a.target
                ? "bg-black-800 text-white"
                : "bg-black-20 text-black-600 hover:bg-black-40"
            }`}
          >
            {getTargetLabel(a.target)}
          </button>
        ))}
      </div>
    );
  };
  const renderThumb = (item, size = "w-10 h-10") => {
    if (item.image_url) {
      return <img src={item.image_url} alt="post" className={`${size} rounded object-cover flex-shrink-0`} />;
    }
    if (item.video_id || item.video_url) {
      return <VideoThumb posterUrl={item.video_poster_url} size={size} />;
    }
    if (item.document_url) {
      return (
        <div className={`${size} rounded bg-black-20 flex items-center justify-center flex-shrink-0`}>
          <span className="text-lg">📄</span>
        </div>
      );
    }
    return null;
  };
  // ===== LIST VIEW =====
  const renderList = () => {
    const monthDatePosts = [...scheduledPosts, ...publishedPosts].sort(
      (a, b) => new Date(a.publication_date_time) - new Date(b.publication_date_time)
    );
    const filteredList = (() => {
      switch (listFilter) {
        case "draft": return drafts;
        case "scheduled": return scheduledPosts;
        case "published": return publishedPosts;
        default: return [...drafts, ...monthDatePosts];
      }
    })();
    return (
      <>
        <h2 className="text-2xl sm:text-3xl font-bold text-black-600 mb-4">
          {t("draft.scheduledPost")}
        </h2>

        {/* Month navigation */}
        <div className="flex flex-wrap items-center justify-between mb-4 text-black-800 font-bold">
          <Button
            variant="ghost"
            onClick={() => setCurrentListMonth(prev => subMonths(new Date(prev), 1))}
            className="border-black-800 hover:border-black-800 gap-2"
          >
            <img src={arrowLeft} alt="prev" style={{ filter: "brightness(0)" }} />
            <span>{getShortMonthName(subMonths(currentListMonth, 1).getMonth())}</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => setCurrentListMonth(prev => addMonths(new Date(prev), 1))}
            className="border-black-800 hover:border-black-800 gap-2 md:order-3"
          >
            <span>{getShortMonthName(addMonths(currentListMonth, 1).getMonth())}</span>
            <img src={arrowRight} alt="next" style={{ filter: "brightness(0)" }} />
          </Button>

          <span className="w-full text-center mt-3 md:w-auto md:mt-0 md:order-2 text-md md:text-lg">
            {getMonthName(currentListMonth.getMonth())} {currentListMonth.getFullYear()}
          </span>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {["all", "draft", "scheduled", "published"].map(f => (
            <button
              key={f}
              onClick={() => setListFilter(f)}
              className={`px-3 py-1 text-xs font-bold rounded-lg capitalize transition-all ${
                listFilter === f
                  ? "bg-black-800 text-white"
                  : "bg-black-20 text-black-600 hover:bg-black-40"
              }`}
            >
              {t(`filter.${f}`, f.charAt(0).toUpperCase() + f.slice(1))}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        {/*<div className="mb-4 border border-black-600 rounded-lg bg-black-20">
          <div className="flex gap-2 items-center p-2">
            <button className="w-11 h-11 flex items-center justify-center border border-black-600 rounded bg-black-0 hover:bg-black-20 group relative">
              <img src={create} alt="new post" className="w-4 h-4" />
            </button>
            <button className="w-11 h-11 flex items-center justify-center border border-black-600 rounded bg-black-0 hover:bg-black-20 group relative">
              <img src={delet} alt="delete" className="w-4 h-4" />
            </button>
          </div>
        </div>*/}

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && filteredList.length === 0 && (
          <div className="text-center py-12 text-black-400">
            <p className="text-lg font-bold">{t("draft.noPostsYet")}</p>
            <p className="text-sm mt-1">{t("draft.noPostsMessage")}</p>
          </div>
        )}

        {/* Post list */}
        {!isLoading && (
          <div className="space-y-3 overflow-hidden">
            {filteredList.map((item) => (
              <div
                key={item.id}
                onClick={() => handleOpenPost(item)}
                className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border-2 border-black-40 rounded-lg px-3 py-3 bg-black-0 text-black-800 hover:!border-primary-300 hover:!bg-primary-200 cursor-pointer transition-all"
              >
                {/* Row 1 on mobile: checkbox + thumbnail + text */}
                <div className="flex flex-col sm:flex-row gap-2 flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    {/*<input
                      type="checkbox"
                      className="w-4 h-4 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    />*/}
                    {renderThumb(item)}
                  </div>
                  <span className="text-black-600 text-sm w-full">
                    {(() => {
                      const text = item.hook || item.post;
                      const charLimit = isMobile ? 150 : 200;
                      return text.length > charLimit ? `${text.slice(0, charLimit)}...` : text;
                    })()}
                  </span>
                </div>

                {/* Row 2 on mobile: badges + date */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0 sm:ml-auto">
                  {/* Status badge */}
                  {(() => {
                    const status = item.status || (item.publication_date_time ? "Scheduled" : "Draft");
                    const statusStyles = {
                      Draft: "bg-black-20 text-black-600 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                      Scheduled: "bg-tertiary-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                      Published: "bg-success-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                    };
                    return (
                      <span className={`px-2 py-1 text-xs font-bold rounded-lg w-fit ${statusStyles[status] || statusStyles.Draft}`}>
                        {t(`draft.${status.toLowerCase()}Badge`, status)}
                      </span>
                    );
                  })()}

                  {/* Target badge — only for scheduled/published */}
                  {item.status !== "Draft" && item.target && (
                    <span className="px-2 py-1 text-xs font-bold rounded-lg bg-secondary-500 text-black-800 w-fit whitespace-nowrap group-hover:bg-primary-500 group-hover:text-black-800 transition-all">
                      {getTargetLabel(item.target)}
                    </span>
                  )}

                  {/* Date */}
                  {item.publication_date_time && (
                    <span className="text-xs text-black-400 font-bold whitespace-nowrap">
                      {`${getMonthName(new Date(item.publication_date_time).getMonth()).slice(0, 3)} ${format(new Date(item.publication_date_time), "dd · HH:mm")}`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View calendar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 mt-6">
          {/* View calendar */}
          <Button
            variant="ghost"
            onClick={() => setShowCalendar(true)}
            className="w-full md:w-auto gap-2"
          >
            <span>{t("draft.viewCalendar")}</span>
            <img src={calendar} alt="calendar" className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </div>
      </>
    );
  };

  // ===== WEEKLY VIEW (mobile) =====
  const renderWeeklyCalendar = () => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <>
        <h2 className="text-2xl sm:text-3xl font-bold text-black-600 mb-4">
          {t("draft.scheduledPost")}
        </h2>

        {/* Week navigation */}
        <div className="flex flex-col items-center gap-2 mb-4 font-bold">
          {/* Prev/Next buttons — second line, full width */}
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              className="border-black-800 hover:border-black-800 gap-2"
            >
              <img src={arrowLeft} alt="prev" style={{ filter: "brightness(0)" }} />
              <span className="text-sm">
                {`${format(subWeeks(currentWeek, 1), "dd")} ${getShortMonthName(subWeeks(currentWeek, 1).getMonth())}`}
              </span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              className="border-black-800 hover:border-black-800 gap-2"
            >
              <span className="text-sm">
                  {`${format(addWeeks(currentWeek, 1), "dd")} ${getShortMonthName(addWeeks(currentWeek, 1).getMonth())}`}
              </span>
              <img src={arrowRight} alt="next" style={{ filter: "brightness(0)" }} />
            </Button>
          </div>
          {/* Date in the middle — first line */}
          <span className="font-bold text-black-800 text-md">
            {format(weekStart, "dd")} – {format(weekEnd, "dd")} {getMonthName(weekEnd.getMonth())} {weekEnd.getFullYear()}
          </span>
        </div>
        {renderProfileFilter()}

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Weekly grid */}
        {!isLoading && (
          <div className="flex flex-col gap-2">
            {days.map((day) => {
              const dateKey = format(day, "MMM dd");
              const events = scheduledMap[dateKey] || [];
              const isToday = format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

              return (
                <div
                  key={day.toString()}
                  className={`border rounded-xl p-3 min-h-[64px] ${
                    isToday ? "!border-primary-500 bg-primary-50" : "border-black-40"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-black-400">
                      {getDayName(day)}
                    </span>
                    <span className="text-lg font-bold text-black-800">
                      {format(day, "dd")}
                    </span>
                  </div>

                  {/* Events */}
                  <div className="flex flex-col gap-2">
                    {events.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => setSelectedEvent(ev)}
                        className="w-full text-left border rounded-lg p-2 text-xs font-bold truncate transition-all text-black-800 bg-black-0 border-black-40 hover:!border-primary-300 hover:!bg-primary-200"
                      >
                        {(ev.hook || ev.post || "").slice(0, 40)}...
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View list */}
        <div className="flex justify-end mt-6">
          <Button
            variant="ghost"
            onClick={() => setShowCalendar(false)}
            className="w-full md:w-auto gap-2"
          >
            <span>{t("draft.viewList")}</span>
            <img src={list} alt="list" className="w-4 h-4" />
          </Button>
        </div>

        {/* Mobile post modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black-900 bg-opacity-50 flex items-end justify-center z-50 md:hidden">
            <div className="bg-white rounded-t-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <span className="font-bold text-lg text-black-800">{t("draft.preview")}</span>
                <button onClick={() => setSelectedEvent(null)}>
                  <img src={close} alt="close" className="w-5 h-5" />
                </button>
              </div>

              {renderThumb(selectedEvent, "w-full h-48 mb-4", "rounded-lg")}

              <p className="text-black-600 text-sm mb-4">
                {selectedEvent.hook || selectedEvent.post}
              </p>

              {/* Status badges */}
              <div className="flex gap-2 flex-wrap mb-4">
                {(() => {
                  const statusStyles = {
                    Draft: "bg-black-20 text-black-600 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                    Scheduled: "bg-tertiary-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                    Published: "bg-success-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                  };
                  const status = selectedEvent.status || "Draft";
                  return (
                    <span className={`px-2 py-1 text-xs font-bold rounded-lg ${statusStyles[status] || statusStyles.Draft}`}>
                      {t(`draft.${status.toLowerCase()}Badge`, status)}
                    </span>
                  );
                })()}
                {selectedEvent.status !== "Draft" && selectedEvent.target && (
                  <span className="px-2 py-1 text-xs font-bold rounded-lg bg-secondary-500 text-black-800">
                    {getTargetLabel(selectedEvent.target)}
                  </span>
                )}
              </div>

              {selectedEvent.publication_date_time && (
                <p className="text-xs text-black-400 font-bold mb-4">
                  {`${getMonthName(new Date(selectedEvent.publication_date_time).getMonth()).slice(0, 3)} ${format(new Date(selectedEvent.publication_date_time), "dd, yyyy · HH:mm")}`}
                </p>
              )}

              <Button
                variant="dark"
                onClick={() => { const ev = selectedEvent; setSelectedEvent(null); handleOpenPost(ev); }}
                className="w-full gap-2"
              >
                <img src={eye} alt="eye" className="w-4 h-4 brightness-0 invert" />
                {t("buttons.viewPost")}
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ===== MONTHLY VIEW (desktop) =====
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const weekStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <>
        <h2 className="text-2xl sm:text-3xl font-bold text-black-600 mb-4">
          {t("draft.scheduledPost")}
        </h2>

        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4 font-bold">
          <Button
            variant="ghost"
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="border-black-800 hover:border-black-800 gap-2"
          >
            <img src={arrowLeft} alt="arrowLeft" style={{ filter: "brightness(0)" }} />
            <span>{getShortMonthName(subMonths(currentDate, 1).getMonth())}</span>
          </Button>
          <span className="text-md md:text-lg">{getMonthName(currentDate.getMonth())} {currentDate.getFullYear()}</span>
          <Button
            variant="ghost"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="border-black-800 hover:border-black-800 gap-2"
          >
            <span>{getShortMonthName(addMonths(currentDate, 1).getMonth())}</span>
            <img src={arrowRight} alt="arrowRight" style={{ filter: "brightness(0)" }} />
          </Button>
        </div>
        {renderProfileFilter()}

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Calendar Grid */}
        {!isLoading && (
          <div className="grid grid-cols-7 gap-2">
            {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
              <div key={d} className="font-bold border border-black-40 bg-black-20 rounded-lg px-2 md:px-4 py-2 text-center">
                <span className="md:hidden">{t(`calendar.weekdays.${d}`).charAt(0)}</span>
                <span className="hidden md:inline">{t(`calendar.weekdays.${d}`)}</span>
              </div>
            ))}

            {days.map((day, dayIndex) => {
              const dateKey = format(day, "MMM dd");
              const events = scheduledMap[dateKey] || [];
              const colInWeek = dayIndex % 7;
              const isRightEdge = colInWeek >= 5;
              const isToday = format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

              return (
                <div
                  key={day.toString()}
                  className={`h-auto min-h-[80px] border rounded-lg p-2 text-sm relative hover:border-primary-400 ${
                    format(day, "MM") !== format(currentDate, "MM") ? "opacity-30" :
                    isToday ? "!border-primary-500 bg-primary-50" : "border-black-40"
                  }`}
                >
                  <div className="absolute top-1 left-1 text-lg font-bold text-black-600">
                    {format(day, "dd")}
                  </div>
                  <div className="mt-7 space-y-1">
                    {events.map((ev) => (
                      <div
                        key={ev.id}
                        className="relative border text-xs p-1 rounded-lg text-left overflow-visible cursor-pointer transition-all text-black-800 bg-black-0 border-black-40 hover:!border-primary-300 hover:!bg-primary-200"
                        onMouseEnter={() => setHoveredEvent(ev)}
                      >
                        <span className="block truncate w-full">
                          {(ev.hook || ev.post || "").slice(0, 20)}...
                        </span>

                        {/* Desktop popover */}
                        {hoveredEvent?.id === ev.id && (
                          <div
                            className={`absolute top-full mt-1 z-[60] w-[320px] bg-white border border-black-40 rounded-lg shadow-lg cursor-default ${
                              isRightEdge ? "right-0" : "left-0"
                            }`}
                            onMouseLeave={() => setHoveredEvent(null)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-between items-center border-b p-3">
                              <span className="font-bold text-lg text-black-800">{t("draft.preview")}</span>
                              <div className="flex items-center space-x-2">
                                {ev.publication_date_time && (
                                  <span className="text-sm text-black-400 font-bold">
                                    {format(new Date(ev.publication_date_time), "HH:mm")}
                                  </span>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setHoveredEvent(null); }}
                                  className="cursor-pointer"
                                >
                                  <img src={close} alt="close" />
                                </button>
                              </div>
                            </div>
                            <div className="p-3 flex space-x-3">
                              {renderThumb(ev, "w-16 h-16 border-2")}
                              <div className="flex flex-col flex-1 gap-2">
                                <p className="text-sm text-black-600 line-clamp-4">
                                  {ev.hook || ev.post || ""}
                                </p>
                                {/* Badges */}
                                <div className="flex gap-2 flex-wrap">
                                  {(() => {
                                    const status = ev.status || "Scheduled";
                                    const statusStyles = {
                                      Draft: "bg-black-20 text-black-600 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                                      Scheduled: "bg-tertiary-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                                      Published: "bg-success-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                                    };
                                    return (
                                      <span className={`px-2 py-1 text-xs font-bold rounded-lg ${statusStyles[status] || statusStyles.Draft}`}>
                                        {t(`draft.${status.toLowerCase()}Badge`, status)}
                                      </span>
                                    );
                                  })()}
                                  {ev.status !== "Draft" && ev.target && (
                                    <span className="px-2 py-1 text-xs font-bold rounded-lg bg-secondary-500 text-black-800">
                                      {getTargetLabel(ev.target)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-end flex-shrink-0">
                                <Button variant="icon" onClick={(e) => { e.stopPropagation(); handleOpenPost(ev); }}>
                                  <img src={eye} alt="eye" className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View list */}
        <div className="flex justify-end mt-6">
          <Button
            variant="ghost"
            onClick={() => setShowCalendar(false)}
            className="w-full md:w-auto gap-2"
          >
            <span>{t("draft.viewList")}</span>
            <img src={list} alt="list" className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </div>
      </>
    );
  };

  return (
    <section className="bg-white p-4 rounded-2xl shadow-sm w-full overflow-hidden">
      {showCalendar
        ? isMobile
          ? renderWeeklyCalendar()
          : renderCalendar()
        : renderList()
      }
    </section>
  );
};

export default Scheduled;